/**
 * InactivityTimeout — Signs the user out after 30 minutes of inactivity.
 *
 * Must be rendered inside both AuthProvider and ToastProvider.
 * Listens for mouse, keyboard, and click events to reset the timer.
 * Shows a warning toast 5 minutes before timeout.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from './Toast';

const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_MS = 25 * 60 * 1000; // 25 minutes (5 min before timeout)
const THROTTLE_MS = 1000; // At most one timer reset per second

export function InactivityTimeout() {
  const { isAuthenticated, signOut } = useAuth();
  const { toast } = useToast();

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningShownRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    if (!isAuthenticated) return;

    clearTimers();
    warningShownRef.current = false;

    warningRef.current = setTimeout(() => {
      if (!warningShownRef.current) {
        warningShownRef.current = true;
        toast('Your session will expire in 5 minutes due to inactivity.', 'info');
      }
    }, WARNING_MS);

    timeoutRef.current = setTimeout(() => {
      toast('Session expired due to inactivity.', 'error');
      void signOut();
    }, TIMEOUT_MS);
  }, [isAuthenticated, signOut, toast, clearTimers]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      return;
    }

    resetTimer();

    const events: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'click'];
    let lastReset = 0;
    const handler = () => {
      const now = Date.now();
      if (now - lastReset < THROTTLE_MS) return;
      lastReset = now;
      resetTimer();
    };

    events.forEach((event) => window.addEventListener(event, handler, { passive: true }));

    return () => {
      clearTimers();
      events.forEach((event) => window.removeEventListener(event, handler));
    };
  }, [isAuthenticated, resetTimer, clearTimers]);

  return null;
}
